import { useCallback } from 'react';

import { useImmerReducer } from 'use-immer';

interface CopyMoveState {
  isOpen: boolean;
  selectedItemIds: Set<string>;
}

type CopyMoveAction =
  | { type: 'open'; selectedItemIds: Set<string> }
  | { type: 'close' };

const initialState: CopyMoveState = {
  isOpen: false,
  selectedItemIds: new Set(),
};

function copyMoveDialogReducer(state: CopyMoveState, action: CopyMoveAction) {
  switch (action.type) {
    case 'open': {
      const { selectedItemIds } = action;
      state.isOpen = true;
      state.selectedItemIds = selectedItemIds;
      return state;
    }
    case 'close': {
      state.isOpen = false;
      state.selectedItemIds = new Set();
      return state;
    }
    default:
      return state;
  }
}

export type CloseDialog = (success: boolean, newParentId?: number) => void;
export type OpenDialog = (...selectedQuizItems: string[]) => void;

export function useCopyQuizToWorkspaceDialog(
  selectNone: () => void,
  setParentId: (newParentId: number) => void
): {
  state: CopyMoveState;
  close: (success: boolean, newParentId?: number) => void;
  open: (...selectedQuizItems: string[]) => void;
} {
  const [state, dispatch] = useImmerReducer<CopyMoveState, CopyMoveAction>(
    copyMoveDialogReducer,
    initialState
  );

  const close: CloseDialog = useCallback(
    (success: boolean, newParentId?: number) => {
      if (success) {
        setParentId(newParentId);
        selectNone();
      }

      dispatch({ type: 'close' });
    },
    [dispatch, selectNone, setParentId]
  );

  const open: OpenDialog = useCallback(
    (...selectedQuizItems: string[]) => {
      dispatch({
        type: 'open',
        selectedItemIds: new Set(selectedQuizItems),
      });
    },
    [dispatch]
  );

  return {
    state: state,
    close: close,
    open: open,
  };
}
